/**
 * UnitCountsSection - Renders unit counts by brand for New/Used Vehicle departments
 *
 * Extracted from ProjectionInputView for maintainability.
 * Shows historical unit counts per brand with editable projection inputs.
 */

import React from 'react';
import { UnitCountsData } from '../../services/api';
import { BrandUnitInputCell } from './ProjectionInputCells';

interface YearUnitData {
  unitCountsData: UnitCountsData | null;
}

interface UnitCountsSectionProps {
  deptName: string;
  /** One entry per display-year column, oldest first */
  yearData: YearUnitData[];
  projectedBrandUnits: Record<string, number>;
  totalProjectedUnits: number;
  isEditing: boolean;
  localInputs: Record<string, { value: number; inputType: 'amount' | 'percentage'; isDirty: boolean }>;
  onValueChange: (department: string, metric: string, value: number, inputType?: 'amount' | 'percentage') => void;
}

/**
 * Renders unit counts by brand with per-brand input capability
 */
export const UnitCountsSection: React.FC<UnitCountsSectionProps> = ({
  deptName,
  yearData,
  projectedBrandUnits,
  totalProjectedUnits,
  isEditing,
  localInputs,
  onValueChange,
}) => {
  const isNewVehicle = deptName === 'NEW_VEHICLE';
  const isUsedVehicle = deptName === 'USED_VEHICLE';

  if (!isNewVehicle && !isUsedVehicle) return null;

  // Units by brand and totals for each column
  const unitsByYear = yearData.map(y => (isNewVehicle
    ? y.unitCountsData?.newVehicleUnits
    : y.unitCountsData?.usedVehicleUnits) || {});
  const totalsByYear = yearData.map(y => (isNewVehicle
    ? y.unitCountsData?.totalNewUnits
    : y.unitCountsData?.totalUsedUnits) || 0);
  const lastIdx = yearData.length - 1;

  // Get all unique brands across all display years (incl. brands first seen in a partial year)
  const allBrands = new Set(unitsByYear.flatMap(u => Object.keys(u)));

  // If no brands, don't render anything
  if (allBrands.size === 0) return null;

  // Sort brands by the newest year's count (descending), then the next newest, etc.
  const sortedBrands = Array.from(allBrands).sort((a, b) => {
    for (let i = lastIdx; i >= 0; i--) {
      const diff = (unitsByYear[i][b] || 0) - (unitsByYear[i][a] || 0);
      if (diff !== 0) return diff;
    }
    return 0;
  });

  const colorClass = isNewVehicle ? 'bg-blue-50 text-blue-900' : 'bg-amber-50 text-amber-900';
  const headerColorClass = isNewVehicle ? 'bg-blue-100 text-blue-900' : 'bg-amber-100 text-amber-900';
  const totalColorClass = isNewVehicle ? 'bg-blue-200 text-blue-900' : 'bg-amber-200 text-amber-900';

  return (
    <>
      {/* Units Header */}
      <tr className={headerColorClass}>
        <td colSpan={2 + yearData.length * 2} className={`px-3 py-2 text-sm font-bold ${headerColorClass}`}>
          Units Sold by Brand
        </td>
      </tr>
      {/* Individual Brand Rows */}
      {sortedBrands.map(brand => {
        const value = projectedBrandUnits[brand] || null;
        const key = `${deptName}#UNITS_${brand}`;
        const isDirty = localInputs[key]?.isDirty || false;

        return (
          <tr key={`unit-${brand}`} className={`${colorClass} border-b border-gray-100`}>
            <td className={`min-w-[200px] px-3 py-1.5 text-xs sticky left-0 ${colorClass}`} style={{ paddingLeft: '24px' }}>
              {brand}
            </td>
            {unitsByYear.map((units, i) => {
              const count = units[brand] || 0;
              return (
                <React.Fragment key={i}>
                  <td className={`min-w-[100px] px-2 py-1.5 text-right text-xs ${i === lastIdx ? 'font-medium' : ''}`}>
                    {count > 0 ? count.toLocaleString() : '--'}
                  </td>
                  <td className="min-w-[60px]"></td>
                </React.Fragment>
              );
            })}
            <BrandUnitInputCell
              department={deptName}
              brand={brand}
              value={value}
              isEditing={isEditing}
              isDirty={isDirty}
              colorClass={colorClass}
              onValueChange={onValueChange}
            />
          </tr>
        );
      })}
      {/* Total Units Row (calculated from brand inputs) */}
      <tr className={totalColorClass}>
        <td className={`min-w-[200px] px-3 py-2 text-sm font-bold sticky left-0 ${totalColorClass}`}>
          Total Units
        </td>
        {totalsByYear.map((total, i) => (
          <React.Fragment key={i}>
            <td className={`min-w-[100px] px-2 py-2 text-right text-sm ${i === lastIdx ? 'font-bold' : 'font-semibold'}`}>
              {total.toLocaleString()}
            </td>
            <td className="min-w-[60px]"></td>
          </React.Fragment>
        ))}
        {/* Auto-calculated total (not editable) */}
        <td className={`min-w-[120px] px-2 py-2 text-right text-sm font-bold ${totalColorClass}`}>
          {totalProjectedUnits > 0 ? totalProjectedUnits.toLocaleString() : '--'}
        </td>
      </tr>
    </>
  );
};
